import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ModuleHeader } from '@/design-system/dna';
import { Palette } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { QRCodeCanvas } from 'qrcode.react';
import { Loader2, Printer, FileDown, Search, ShieldCheck, ScrollText, Pencil, Save, RefreshCw, UserPlus, ChevronRight, Users } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const apiBase = `${API}/api/historico-escolar`;

const SERIES = ['1ª série', '2ª série', '3ª série'];
const AREAS = ['Linguagens', 'Matemática', 'Ciências da Natureza', 'Ciências Humanas', 'Itinerários / Parte Diversificada'];

export default function HistoricoEscolar() {
  const { studentId } = useParams();
  const navigate = useNavigate();
  const [turmas, setTurmas] = useState([]);
  const [selectedTurma, setSelectedTurma] = useState(null);
  const [students, setStudents] = useState([]);
  const [search, setSearch] = useState('');
  const [loadingTurmas, setLoadingTurmas] = useState(true);
  const [loadingList, setLoadingList] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [fonte, setFonte] = useState('dnacore');
  const [editing, setEditing] = useState(false);
  const [manualRows, setManualRows] = useState([]);
  const [manualMeta, setManualMeta] = useState({ situacao_final: 'Cursando', frequencia_pct: 95, observacoes: '' });
  const [saving, setSaving] = useState(false);
  const [emissao, setEmissao] = useState(null);
  const [isExterno, setIsExterno] = useState(false);
  const [externoForm, setExternoForm] = useState({});
  const [config, setConfig] = useState({});
  const [showLogoDialog, setShowLogoDialog] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const printRef = useRef(null);

  const token = localStorage.getItem('token') || localStorage.getItem('access_token');
  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  const loadTurmas = async () => {
    setLoadingTurmas(true);
    try {
      const r = await axios.get(`${apiBase}/turmas`, { headers });
      setTurmas(r.data || []);
    } catch (e) { toast.error('Falha ao carregar turmas'); }
    finally { setLoadingTurmas(false); }
  };

  const loadConfig = async () => {
    try {
      const r = await axios.get(`${apiBase}/_config`, { headers });
      setConfig(r.data || {});
    } catch { /* ignore */ }
  };

  const uploadLogo = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const allowed = ['image/png', 'image/jpeg', 'image/jpg', 'image/svg+xml', 'image/webp'];
    if (!allowed.includes(file.type)) { toast.error('Formato inválido. Use PNG, JPG, SVG ou WEBP.'); e.target.value = ''; return; }
    if (file.size > 4 * 1024 * 1024) { toast.error('Logo maior que 4MB.'); e.target.value = ''; return; }
    setUploadingLogo(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      await axios.post(`${apiBase}/_config/logo`, fd, { headers: { ...headers, 'Content-Type': 'multipart/form-data' } });
      toast.success('Logo atualizado');
      loadConfig();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Falha no upload'); }
    finally { setUploadingLogo(false); e.target.value = ''; }
  };

  const removerLogo = async () => {
    try {
      await axios.delete(`${apiBase}/_config/logo`, { headers });
      toast.success('Logo restaurado para padrão');
      loadConfig();
    } catch { toast.error('Falha ao remover'); }
  };

  const toggleMonocromatico = async (v) => {
    try {
      await axios.put(`${apiBase}/_config`, { logo_monocromatico: v }, { headers });
      loadConfig();
    } catch { /* ignore */ }
  };

  // PATCH: salvar dados institucionais (CNPJ, INEP, Autorização, etc.)
  const [schoolForm, setSchoolForm] = useState({});
  const [savingSchool, setSavingSchool] = useState(false);
  useEffect(() => {
    setSchoolForm({
      school_nome: config.school_nome || '',
      school_cnpj: config.school_cnpj || '',
      school_inep: config.school_inep || '',
      school_autorizacao: config.school_autorizacao || '',
      school_endereco: config.school_endereco || '',
      school_telefone: config.school_telefone || '',
      school_cidade_uf: config.school_cidade_uf || '',
    });
  }, [config]);
  const saveSchoolConfig = async () => {
    setSavingSchool(true);
    try {
      await axios.put(`${apiBase}/_config`, schoolForm, { headers });
      toast.success('Dados da escola atualizados');
      loadConfig();
      if (studentId) loadHistorico(studentId);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Falha ao salvar dados da escola');
    } finally { setSavingSchool(false); }
  };

  const loadStudents = async (cgId = null, q = null) => {
    setLoadingList(true);
    try {
      const params = {};
      if (cgId) params.class_group_id = cgId;
      if (q) params.q = q;
      const r = await axios.get(`${apiBase}/students`, { params, headers });
      setStudents(r.data || []);
    } catch (e) {
      toast.error('Falha ao carregar alunos');
    } finally { setLoadingList(false); }
  };

  const loadHistorico = async (id) => {
    if (!id) { setData(null); return; }
    const isExt = String(id).startsWith('ext-');
    setIsExterno(isExt);
    setLoading(true);
    try {
      const url = isExt ? `${apiBase}/externo/${id}/historico` : `${apiBase}/${id}`;
      const r = await axios.get(url, { headers });
      setData(r.data);
      // PATCH: prioridade — manual salvo > quadro_auto (notas reais) > skeleton EM pré-carregado
      const m = r.data?.manual?.rows || [];
      const auto = r.data?.quadro_auto || [];
      const skeleton = r.data?.skeleton_rows || [];
      if (m.length > 0) {
        setManualRows(m);
      } else if (auto.length > 0) {
        setManualRows(auto.map(row => ({
          subject_name: row.subject_name,
          area: row.area,
          carga_horaria: row.carga_horaria,
          nota_1: row.notas['1ª série'],
          nota_2: row.notas['2ª série'],
          nota_3: row.notas['3ª série'],
        })));
      } else {
        setManualRows(skeleton);
      }
      if (r.data?.manual) {
        setManualMeta({
          situacao_final: r.data.manual.situacao_final || (isExt ? 'Aprovado' : 'Cursando'),
          frequencia_pct: r.data.manual.frequencia_pct ?? 95,
          observacoes: r.data.manual.observacoes || '',
        });
      }
      if (isExt) {
        setExternoForm({
          nome: r.data.student?.nome || '',
          ra: r.data.student?.ra || '',
          rm: r.data.student?.rm || '',
          cpf: r.data.student?.cpf || '',
          rg: r.data.student?.rg || '',
          birth_date: r.data.student?.nascimento || '',
          municipio: r.data.student?.naturalidade_municipio || '',
          uf: r.data.student?.naturalidade_uf || '',
          nacionalidade: r.data.student?.nacionalidade || 'Brasileira',
          filiacao_pai: r.data.student?.filiacao_pai || '',
          filiacao_mae: r.data.student?.filiacao_mae || '',
          turma_referencia: r.data.student?.turma_atual || '',
        });
        setFonte('manual');
        setEditing(true);
      }
    } catch (e) {
      toast.error('Falha ao carregar histórico');
    } finally { setLoading(false); }
  };

  useEffect(() => { loadTurmas(); loadConfig(); /* eslint-disable-next-line */ }, []);
  useEffect(() => { if (studentId) loadHistorico(studentId); /* eslint-disable-next-line */ }, [studentId]);
  // Recarrega lista quando muda turma OU busca
  useEffect(() => {
    if (search) loadStudents(null, search);
    else if (selectedTurma) loadStudents(selectedTurma);
    else setStudents([]);
    /* eslint-disable-next-line */
  }, [selectedTurma, search]);

  const filteredStudents = students; // backend já filtra

  const handleSelectStudent = (id) => navigate(`/v2/secretaria/historico/${id}`);

  const criarLancamentoManual = async () => {
    try {
      const r = await axios.post(`${apiBase}/externo`, {}, { headers });
      const id = r.data?.id;
      if (!id) throw new Error('sem id');
      toast.success('Histórico manual criado — preencha os dados do aluno externo');
      navigate(`/v2/secretaria/historico/${id}`);
    } catch (e) {
      toast.error('Falha ao criar lançamento manual');
    }
  };

  const salvarExterno = async () => {
    if (!studentId || !isExterno) return;
    try {
      await axios.put(`${apiBase}/externo/${studentId}`, externoForm, { headers });
      toast.success('Dados do aluno externo salvos');
      loadHistorico(studentId);
    } catch (e) {
      toast.error('Falha ao salvar dados do aluno');
    }
  };

  const rows = useMemo(() => {
    if (fonte === 'manual') return manualRows;
    return (data?.quadro_auto || []).map(r => ({
      subject_name: r.subject_name,
      area: r.area,
      carga_horaria: r.carga_horaria,
      nota_1: r.notas['1ª série'],
      nota_2: r.notas['2ª série'],
      nota_3: r.notas['3ª série'],
    }));
  }, [fonte, data, manualRows]);

  const rowsByArea = useMemo(() => {
    const groups = AREAS.map(a => ({ area: a, items: [] }));
    rows.forEach(r => {
      const g = groups.find(x => x.area === (r.area || 'Itinerários / Parte Diversificada'));
      (g || groups[groups.length - 1]).items.push(r);
    });
    return groups.filter(g => g.items.length > 0);
  }, [rows]);

  const addManualRow = () => setManualRows(rs => [...rs, { subject_name: '', area: AREAS[0], carga_horaria: 80, nota_1: null, nota_2: null, nota_3: null }]);
  const removeManualRow = (i) => setManualRows(rs => rs.filter((_, idx) => idx !== i));
  const updateManualRow = (i, field, value) => setManualRows(rs => rs.map((r, idx) => idx === i ? { ...r, [field]: value } : r));

  const salvarManual = async () => {
    if (!studentId) return;
    setSaving(true);
    try {
      const payload = {
        rows: manualRows.map(r => ({
          subject_name: r.subject_name || '',
          area: r.area || null,
          carga_horaria: r.carga_horaria == null || r.carga_horaria === '' ? null : Number(r.carga_horaria),
          nota_1: r.nota_1 == null || r.nota_1 === '' ? null : Number(r.nota_1),
          nota_2: r.nota_2 == null || r.nota_2 === '' ? null : Number(r.nota_2),
          nota_3: r.nota_3 == null || r.nota_3 === '' ? null : Number(r.nota_3),
        })).filter(r => r.subject_name),
        observacoes: manualMeta.observacoes || null,
        situacao_final: manualMeta.situacao_final || null,
        frequencia_pct: manualMeta.frequencia_pct == null ? null : Number(manualMeta.frequencia_pct),
      };
      await axios.put(`${apiBase}/${studentId}/manual`, payload, { headers });
      toast.success('Lançamento manual salvo');
      setEditing(false);
      loadHistorico(studentId);
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || 'Falha ao salvar histórico');
    } finally { setSaving(false); }
  };

  const emitirHistorico = async () => {
    if (!studentId) return;
    try {
      const r = await axios.post(`${apiBase}/${studentId}/emitir`, {
        fonte,
        situacao_final: manualMeta.situacao_final,
      }, { headers });
      setEmissao(r.data);
      toast.success('Histórico emitido. QR de validação ativo.');
    } catch (e) {
      toast.error('Falha ao emitir');
    }
  };

  const validarUrl = emissao?.token ? `${window.location.origin}/v2/secretaria/historico/validar/${emissao.token}` : '';

  const handlePrint = () => window.print();

  if (loading) {
    return (
      <div className="p-8 text-center text-slate-500">
        <Loader2 className="w-6 h-6 animate-spin inline mr-2" /> Carregando histórico…
      </div>
    );
  }

  return (
    <div className="space-y-4 p-4 md:p-6">
      {/* CSS de impressão PREMIUM (A4 acadêmico institucional) */}
      <style>{`
        :root { --hist-purple: #3F1F5C; --hist-purple-2: #64327D; --hist-emerald: #047857; --hist-gold: #b08800; }
        .doc-paper { background: #fff; box-shadow: 0 4px 24px rgba(15, 23, 42, 0.08); }
        .doc-a4 { width: 210mm; min-height: 297mm; margin: 0 auto; padding: 14mm 13mm 16mm 13mm; box-sizing: border-box; position: relative; }
        .doc-serif { font-family: "Cormorant Garamond","Times New Roman", Georgia, serif; }
        .doc-sans  { font-family: "Inter","Segoe UI", system-ui, sans-serif; }
        .doc-rule-grad { background: linear-gradient(90deg, transparent, var(--hist-purple) 8%, var(--hist-purple-2) 50%, var(--hist-purple) 92%, transparent); }
        .doc-rule-thin { border-top: 0.5px solid #cbd5e1; }
        .doc-card { border: 1px solid #e2e8f0; border-radius: 6px; background: #fff; }
        /* Tabela ultra-suave (estilo acadêmico Cambridge/IB) */
        .doc-table { border-collapse: collapse; width: 100%; table-layout: fixed; }
        .doc-table th, .doc-table td { border-bottom: 1px solid #eef2f7; padding: 5px 7px; vertical-align: middle; }
        .doc-table thead th { background: var(--hist-purple); color: #fff; font-weight: 600; letter-spacing: 0.06em; text-transform: uppercase; font-size: 8.5px; border-bottom: 0; border-right: 1px solid rgba(255,255,255,0.12); }
        .doc-table thead th:last-child { border-right: 0; }
        .doc-table tbody td { font-size: 10.5px; }
        .doc-table tbody tr + tr td { border-top: 1px solid #f1f5f9; }
        .doc-area-cell { background: #faf7ff; border-right: 1px solid #ede4ff !important; }
        .doc-table tfoot td { border-top: 1px solid #ddd6fe; border-bottom: 0; }
        /* Selos */
        .doc-seal { position: absolute; right: 16mm; bottom: 56mm; transform: rotate(-8deg); border: 1.5px solid var(--hist-purple); color: var(--hist-purple); border-radius: 999px; padding: 5px 14px; font-size: 9px; letter-spacing: 0.28em; font-weight: 700; background: rgba(255,255,255,0.55); opacity: 0.85; z-index: 5; pointer-events: none; }
        .doc-side-stamp { position: absolute; left: 6mm; top: 50%; transform: translateY(-50%) rotate(-90deg); transform-origin: center; font-size: 8px; letter-spacing: 0.42em; font-weight: 700; color: var(--hist-purple); opacity: 0.55; white-space: nowrap; pointer-events: none; }
        .doc-side-stamp::before, .doc-side-stamp::after { content: ""; display: inline-block; width: 18px; height: 1px; background: var(--hist-purple); vertical-align: middle; margin: 0 8px; opacity: 0.5; }
        /* Marca d'água (DNA) — visível apenas na impressão */
        .doc-watermark { position: absolute; inset: 0; display: flex; align-items: center; justify-content: center; pointer-events: none; opacity: 0; z-index: 0; }
        .doc-watermark svg { width: 140mm; height: auto; }
        /* Faixa institucional final */
        .doc-band {
          margin-top: 18px; position: relative; padding: 14px 18px;
          background: linear-gradient(180deg, #fbfaff 0%, #ffffff 100%);
          border: 1px solid #ede4ff; border-radius: 4px;
          text-align: center;
        }
        .doc-band::before, .doc-band::after {
          content: ""; position: absolute; left: 18%; right: 18%; height: 1px;
          background: linear-gradient(90deg, transparent, var(--hist-purple) 50%, transparent);
        }
        .doc-band::before { top: 0; }
        .doc-band::after { bottom: 0; }
        .doc-band-dot { display: inline-block; width: 4px; height: 4px; background: var(--hist-emerald); border-radius: 999px; vertical-align: middle; margin: 0 8px; }

        @media print {
          @page { size: A4 portrait; margin: 0; }
          html, body { background: #fff !important; margin: 0; padding: 0; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          body * { visibility: hidden; }
          .doc-print, .doc-print * { visibility: visible; }
          .doc-print { position: absolute; left: 0; top: 0; width: 100%; }
          .doc-print .doc-paper { box-shadow: none !important; border: 0 !important; }
          .doc-a4 { width: 210mm; min-height: auto; padding: 12mm 12mm 16mm 12mm; box-shadow: none; }
          .no-print { display: none !important; }
          .doc-table { page-break-inside: auto; }
          .doc-table thead { display: table-header-group; }
          .doc-table tfoot { display: table-footer-group; }
          .doc-table tr { page-break-inside: avoid; page-break-after: auto; }
          .doc-no-break { page-break-inside: avoid; }
          .doc-watermark { opacity: 0.045; }
        }
      `}</style>

      {/* HEADER da página V2 */}
      <div className="no-print"><ModuleHeader kicker="Secretaria" title="Histórico Escolar — Ensino Médio" subtitle="Emissão e conferência do histórico do estudante" icon={ScrollText} testid="historico-header"
        actions={<>
          <Button variant="outline" size="sm" onClick={() => setShowLogoDialog(true)} data-testid="hist-config-logo">
            <Palette className="w-4 h-4 mr-1" />Identidade Visual
          </Button>
          {data && (<>
            <Button variant="outline" onClick={() => loadHistorico(studentId)} data-testid="hist-refresh"><RefreshCw className="w-4 h-4 mr-1" />Recarregar</Button>
            <Button variant="outline" onClick={handlePrint} data-testid="hist-print"><Printer className="w-4 h-4 mr-1" />Imprimir</Button>
            <Button variant="outline" onClick={handlePrint} data-testid="hist-pdf"><FileDown className="w-4 h-4 mr-1" />Salvar PDF</Button>
            <Button onClick={emitirHistorico} className="dna-mh-primary" data-testid="hist-emitir"><ShieldCheck className="w-4 h-4 mr-1" />Emitir / Validar</Button>
          </>)}
        </>} /></div>

      <div className="grid grid-cols-1 lg:grid-cols-[340px_1fr] gap-4">
        {/* LISTA DE TURMAS + ALUNOS EM */}
        <Card className="border-purple-100 no-print">
          <CardHeader className="pb-2 space-y-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm text-purple-800">Ensino Médio</CardTitle>
              <Button size="sm" onClick={criarLancamentoManual} className="bg-gradient-to-r from-purple-700 to-emerald-600 text-white" data-testid="hist-lancamento-manual">
                <UserPlus className="w-4 h-4 mr-1" />Manual
              </Button>
            </div>
            <div className="relative">
              <Search className="w-4 h-4 absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input placeholder="Buscar por nome, RA, RM ou CPF" value={search} onChange={(e) => setSearch(e.target.value)} className="pl-8" data-testid="hist-search" />
            </div>
          </CardHeader>
          <CardContent className="space-y-2">
            {/* Cards de turmas */}
            {!search && (
              <div className="space-y-1.5">
                <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold px-1">Turmas</div>
                {loadingTurmas ? (
                  <div className="p-3 text-center text-slate-500"><Loader2 className="w-4 h-4 animate-spin inline" /></div>
                ) : turmas.length === 0 ? (
                  <div className="p-3 text-center text-slate-500 text-xs">Sem turmas EM cadastradas.</div>
                ) : turmas.map(t => (
                  <button
                    key={t.id}
                    onClick={() => setSelectedTurma(selectedTurma === t.id ? null : t.id)}
                    className={`w-full text-left rounded-lg border px-3 py-2 transition flex items-center justify-between ${selectedTurma === t.id ? 'border-purple-400 bg-purple-50' : 'border-slate-200 hover:bg-purple-50/40'}`}
                    data-testid={`hist-turma-${t.id}`}
                  >
                    <div>
                      <div className="text-sm font-semibold text-slate-800">{t.name}</div>
                      <div className="text-[10px] text-slate-500 uppercase tracking-wide flex items-center gap-1"><Users className="w-3 h-3" />{t.total_alunos} aluno(s)</div>
                    </div>
                    <ChevronRight className={`w-4 h-4 transition ${selectedTurma === t.id ? 'rotate-90 text-purple-600' : 'text-slate-400'}`} />
                  </button>
                ))}
              </div>
            )}

            {/* Lista de alunos */}
            {(selectedTurma || search) && (
              <div className="rounded border border-slate-100 mt-2">
                <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold px-2 py-1 bg-slate-50">
                  {search ? `Resultados (${filteredStudents.length})` : `Alunos da turma`}
                </div>
                <div className="max-h-[50vh] overflow-y-auto">
                  {loadingList ? (
                    <div className="p-4 text-center text-slate-500"><Loader2 className="w-4 h-4 animate-spin inline" /></div>
                  ) : filteredStudents.length === 0 ? (
                    <div className="p-4 text-center text-slate-500 text-xs">Nenhum aluno encontrado.</div>
                  ) : (
                    <ul>
                      {filteredStudents.map(s => (
                        <li key={s.id}>
                          <button
                            onClick={() => handleSelectStudent(s.id)}
                            className={`w-full text-left px-3 py-2 text-sm border-b hover:bg-purple-50 ${studentId === s.id ? 'bg-purple-50 font-semibold text-purple-800' : ''}`}
                            data-testid={`hist-student-${s.id}`}
                          >
                            <div className="truncate">{s.name}</div>
                            <div className="text-[10px] text-slate-500 uppercase tracking-wide flex gap-2">
                              <span>{s.class_name || '—'}</span>
                              {s.ra && <span>RA: {s.ra}</span>}
                            </div>
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* DOCUMENTO */}
        <div className="space-y-3">
          {!studentId ? (
            <Card className="border-dashed border-purple-200 bg-purple-50/30">
              <CardContent className="py-16 text-center text-slate-500">
                <ScrollText className="w-10 h-10 mx-auto text-purple-300 mb-2" />
                <p className="font-medium">Selecione um aluno do Ensino Médio para gerar o histórico.</p>
              </CardContent>
            </Card>
          ) : !data ? null : (
            <>
              {/* Controles de fonte e edição */}
              <div className="flex items-center justify-between flex-wrap gap-2 no-print">
                <div className="inline-flex bg-slate-100 rounded-full p-1" role="tablist">
                  <button
                    onClick={() => { setFonte('dnacore'); setEditing(false); }}
                    className={`px-3 py-1.5 rounded-full text-xs font-semibold transition ${fonte === 'dnacore' ? 'bg-purple-700 text-white shadow' : 'text-slate-600'}`}
                    data-testid="hist-fonte-dnacore"
                  >Notas DNACore</button>
                  <button
                    onClick={() => setFonte('manual')}
                    className={`px-3 py-1.5 rounded-full text-xs font-semibold transition ${fonte === 'manual' ? 'bg-emerald-600 text-white shadow' : 'text-slate-600'}`}
                    data-testid="hist-fonte-manual"
                  >Lançamento Manual</button>
                </div>
                {fonte === 'manual' && (
                  editing ? (
                    <div className="flex gap-2">
                      <Button variant="outline" size="sm" onClick={() => setEditing(false)}>Cancelar</Button>
                      <Button size="sm" onClick={salvarManual} disabled={saving} className="bg-emerald-600 text-white" data-testid="hist-salvar-manual">
                        {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}Salvar
                      </Button>
                    </div>
                  ) : (
                    <Button size="sm" variant="outline" onClick={() => setEditing(true)} data-testid="hist-edit-manual"><Pencil className="w-4 h-4 mr-1" />Editar lançamentos</Button>
                  )
                )}
              </div>

              {/* DOCUMENTO PREMIUM */}
              <div ref={printRef} className="doc-print">
                <DocumentoHistorico
                  data={data}
                  rowsByArea={rowsByArea}
                  manualMeta={manualMeta}
                  setManualMeta={setManualMeta}
                  manualRows={manualRows}
                  updateManualRow={updateManualRow}
                  addManualRow={addManualRow}
                  removeManualRow={removeManualRow}
                  fonte={fonte}
                  editing={editing && fonte === 'manual'}
                  emissao={emissao}
                  validarUrl={validarUrl}
                  isExterno={isExterno}
                  externoForm={externoForm}
                  setExternoForm={setExternoForm}
                  salvarExterno={salvarExterno}
                  config={config}
                />
              </div>
            </>
          )}
        </div>
      </div>

      {/* Dialog: Identidade Visual */}
      {showLogoDialog && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4 no-print" onClick={() => setShowLogoDialog(false)}>
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-5" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-lg font-bold text-purple-800">Identidade Visual · Histórico Escolar</h3>
              <button onClick={() => setShowLogoDialog(false)} className="text-slate-400 hover:text-slate-700">×</button>
            </div>
            <div className="space-y-4 max-h-[80vh] overflow-y-auto pr-1">
              <div className="text-xs text-slate-500">Logo usado no preview, PDF, impressão e validação pública. PNG, SVG, JPG ou WEBP · até 4MB.</div>

              <div className="rounded-lg border-2 border-dashed border-purple-200 bg-purple-50/40 p-4 grid place-items-center min-h-[140px]">
                {config?.logo_url ? (
                  <img src={`${API}${config.logo_url}`} alt="preview" className="max-h-28 max-w-[200px] object-contain" data-testid="hist-logo-preview" />
                ) : (
                  <div className="text-center">
                    <div className="text-purple-700 font-black leading-none text-3xl">novo<br />dna</div>
                    <div className="text-emerald-600 font-bold text-[11px] mt-1">school (padrão)</div>
                  </div>
                )}
              </div>

              <div className="flex items-center justify-between">
                <span className="text-sm text-slate-700">Usar logo monocromático no PDF</span>
                <input
                  type="checkbox"
                  checked={!!config?.logo_monocromatico}
                  onChange={(e) => toggleMonocromatico(e.target.checked)}
                  data-testid="hist-logo-mono"
                />
              </div>

              <div className="flex flex-wrap gap-2">
                <input
                  id="hist-logo-input"
                  type="file"
                  accept="image/png,image/jpeg,image/jpg,image/svg+xml,image/webp"
                  className="hidden"
                  onChange={uploadLogo}
                  data-testid="hist-logo-input"
                />
                <Button
                  onClick={() => document.getElementById('hist-logo-input').click()}
                  disabled={uploadingLogo}
                  className="flex-1 bg-gradient-to-r from-purple-700 to-emerald-600 text-white"
                  data-testid="hist-logo-upload"
                >
                  {uploadingLogo ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" />Enviando…</> : config?.logo_url ? 'Substituir logo' : 'Enviar logo'}
                </Button>
                {config?.logo_url && (
                  <Button variant="outline" onClick={removerLogo} className="text-rose-600 border-rose-200" data-testid="hist-logo-remove">
                    Restaurar padrão
                  </Button>
                )}
              </div>
              {config?.updated_at && (
                <div className="text-[11px] text-slate-500">Atualizado em {formatDateTime(config.updated_at)}{config.updated_by ? ` por ${config.updated_by}` : ''}</div>
              )}

              {/* PATCH: Dados Institucionais Editáveis */}
              <div className="pt-3 mt-2 border-t border-slate-200">
                <div className="text-sm font-bold text-purple-800 mb-2">Dados Institucionais</div>
                <div className="grid grid-cols-1 gap-2">
                  {[
                    { k: 'school_nome', label: 'Nome da instituição' },
                    { k: 'school_cnpj', label: 'CNPJ' },
                    { k: 'school_inep', label: 'INEP' },
                    { k: 'school_autorizacao', label: 'Autorização de Funcionamento' },
                    { k: 'school_endereco', label: 'Endereço' },
                    { k: 'school_telefone', label: 'Telefone' },
                    { k: 'school_cidade_uf', label: 'Cidade / UF (rodapé)' },
                  ].map(f => (
                    <div key={f.k}>
                      <label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">{f.label}</label>
                      <Input
                        value={schoolForm[f.k] || ''}
                        onChange={(e) => setSchoolForm(s => ({ ...s, [f.k]: e.target.value }))}
                        className="h-8 text-sm"
                        data-testid={`hist-school-${f.k}`}
                      />
                    </div>
                  ))}
                </div>
                <Button
                  onClick={saveSchoolConfig}
                  disabled={savingSchool}
                  className="w-full mt-3 bg-gradient-to-r from-purple-700 to-emerald-600 text-white"
                  data-testid="hist-school-save"
                >
                  {savingSchool ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" />Salvando…</> : <><Save className="w-4 h-4 mr-1" />Salvar dados da escola</>}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Componente: Documento Premium (matching imagem aprovada) ─────────
const AREA_STYLES = {
  'Linguagens': { label: 'LINGUAGENS', sub: 'E SUAS TECNOLOGIAS', icon: '📖', color: 'purple', tint: 'bg-purple-50/70', text: 'text-purple-700' },
  'Matemática': { label: 'MATEMÁTICA', sub: 'E SUAS TECNOLOGIAS', icon: '📐', color: 'emerald', tint: 'bg-emerald-50/70', text: 'text-emerald-700' },
  'Ciências da Natureza': { label: 'CIÊNCIAS DA NATUREZA', sub: 'E SUAS TECNOLOGIAS', icon: '⚛', color: 'sky', tint: 'bg-sky-50/70', text: 'text-sky-700' },
  'Ciências Humanas': { label: 'CIÊNCIAS HUMANAS', sub: 'E SOCIAIS APLICADAS', icon: '👥', color: 'orange', tint: 'bg-orange-50/70', text: 'text-orange-700' },
  'Itinerários / Parte Diversificada': { label: 'ITINERÁRIOS FORMATIVOS', sub: '(Parte Diversificada)', icon: '🎓', color: 'emerald', tint: 'bg-emerald-50/40', text: 'text-emerald-700' },
};

function avg(...vals) {
  const arr = vals.map(v => v == null || v === '' ? null : Number(v)).filter(v => v != null && !isNaN(v));
  if (!arr.length) return null;
  return Math.round((arr.reduce((a, b) => a + b, 0) / arr.length) * 10) / 10;
}

function DocumentoHistorico({ data, rowsByArea, manualMeta, setManualMeta, manualRows, updateManualRow, addManualRow, removeManualRow, fonte, editing, emissao, validarUrl, isExterno, externoForm, setExternoForm, salvarExterno, config }) {
  const student = data.student;
  const school = data.school;
  const sit = manualMeta.situacao_final || 'Cursando';
  const logoUrl = config?.logo_url ? `${API}${config.logo_url}` : null;
  const mono = !!config?.logo_monocromatico;
  const allRows = rowsByArea.flatMap(g => g.items);
  const mediasFinais = allRows.map(r => avg(r.nota_1, r.nota_2, r.nota_3)).filter(v => v != null);
  const mediaGeral = mediasFinais.length ? Math.round((mediasFinais.reduce((a, b) => a + b, 0) / mediasFinais.length) * 10) / 10 : null;
  const chTotal = allRows.reduce((acc, r) => acc + (Number(r.carga_horaria) || 0), 0);
  const sitFinalText = sit.toUpperCase();
  const sitAprovado = sit === 'Aprovado' || sit === 'Concluído';
  const fmt = (n) => n == null ? '—' : String(Number(n).toFixed(1)).replace('.', ',');

  // Pares "Pai / Mãe" em uma linha de filiação
  const filiacaoLine = [student.filiacao_pai, student.filiacao_mae].filter(Boolean).join(' / ') || null;

  return (
    <div className="doc-paper doc-a4 doc-sans relative text-[10.5px] text-slate-900 print:shadow-none" data-testid="hist-documento">
      {/* Marca d'água DNA — visível apenas na impressão */}
      <div className="doc-watermark" aria-hidden="true">
        <svg viewBox="0 0 240 240" fill="none" stroke="currentColor" strokeWidth="1.2" style={{ color: '#3F1F5C' }}>
          <path d="M60 20 C 120 60, 120 120, 60 160 C 0 200, 120 220, 180 220" />
          <path d="M180 20 C 120 60, 120 120, 180 160 C 240 200, 120 220, 60 220" />
          {Array.from({ length: 10 }).map((_, i) => (
            <line key={i} x1={60 + i * 8} y1={30 + i * 18} x2={180 - i * 8} y2={30 + i * 18} strokeOpacity="0.7" />
          ))}
        </svg>
      </div>

      {/* Selo lateral discreto "VALIDADO DIGITALMENTE" */}
      <div className="doc-side-stamp doc-sans">VALIDADO DIGITALMENTE</div>

      {/* Selo discreto Documento Oficial */}
      <div className="doc-seal doc-sans">DOCUMENTO OFICIAL</div>

      {/* ═════════════ CABEÇALHO ═════════════ */}
      <header className="doc-no-break">
        <div className="grid grid-cols-[88px_1fr_180px] gap-5 items-center">
          {/* Logo institucional grande */}
          <div className="flex items-center justify-center">
            {logoUrl ? (
              <img
                src={logoUrl}
                alt="Brasão institucional"
                className="max-h-[78px] max-w-[88px] object-contain"
                style={{ filter: mono ? 'grayscale(100%)' : 'none' }}
                data-testid="hist-doc-logo"
              />
            ) : (
              <div className="text-center">
                <div className="doc-serif font-black leading-none" style={{ fontSize: 30, letterSpacing: -1, color: 'var(--hist-purple)' }}>
                  novo<br /><span className="inline-block">dna</span>
                </div>
                <div className="font-semibold text-[9px] mt-1 tracking-[0.18em]" style={{ color: 'var(--hist-purple-2)' }}>SCHOOL</div>
              </div>
            )}
          </div>
          {/* Nome + subtítulo */}
          <div className="text-center">
            <div className="doc-serif text-[20px] leading-tight font-bold" style={{ color: 'var(--hist-purple)', letterSpacing: 0.2 }}>
              {(school.nome || 'Novo DNA Instituto de Aprendizagem').toUpperCase()}
            </div>
            <div className="mt-1 text-[10px] tracking-[0.32em] font-semibold text-slate-600 uppercase">Histórico Escolar — Ensino Médio</div>
            <div className="text-[9px] text-slate-500 mt-1.5 leading-snug">
              {[school.endereco, school.telefone].filter(Boolean).join(' · ')}
              {school.endereco || school.telefone ? ' · ' : ''}
              www.novodna.com.br
            </div>
          </div>
          {/* Card institucional minimal */}
          <div className="doc-card px-3 py-2 text-[8.5px] leading-snug text-slate-700">
            <div className="grid grid-cols-[40px_1fr] gap-y-1 gap-x-2">
              <div className="text-slate-500 uppercase tracking-wider">CNPJ</div>
              <div className="font-semibold text-slate-800">{school.cnpj || '—'}</div>
              <div className="text-slate-500 uppercase tracking-wider">INEP</div>
              <div className="font-semibold text-slate-800">{school.inep || '—'}</div>
              <div className="text-slate-500 uppercase tracking-wider">Aut.</div>
              <div className="font-semibold text-slate-800 leading-tight">{school.autorizacao || '—'}</div>
            </div>
          </div>
        </div>
        <div className="mt-3 h-[1.5px] doc-rule-grad rounded-full" />
        <div className="mt-1 h-[0.5px] bg-slate-300" />
      </header>

      {/* ═════════════ DADOS DO ALUNO ═════════════ */}
      <section className="mt-5 doc-no-break">
        <div className="flex items-baseline justify-between mb-2">
          <h2 className="doc-serif text-[13px] font-bold tracking-wide" style={{ color: 'var(--hist-purple)' }}>I — Identificação do Estudante</h2>
          {isExterno && (
            <Button size="sm" variant="outline" onClick={salvarExterno} className="no-print h-6 text-[10px]" data-testid="hist-salvar-externo">
              <Save className="w-3 h-3 mr-1" />Salvar dados
            </Button>
          )}
        </div>
        <div className="doc-card px-5 py-4">
          <div className="grid grid-cols-2 gap-x-8 gap-y-2.5">
            <DataLine label="Nome completo" value={isExterno ? null : student.nome} strong upper name="nome" isExterno={isExterno} form={externoForm} setForm={setExternoForm} colSpan={2} />
            <DataLine label="Filiação" value={isExterno ? null : filiacaoLine} name="filiacao" isExterno={isExterno} form={externoForm} setForm={setExternoForm} colSpan={2} renderExterno={() => (
              <div className="grid grid-cols-2 gap-3 mt-0.5">
                <Input value={externoForm?.filiacao_pai || ''} onChange={(e) => setExternoForm(p => ({ ...p, filiacao_pai: e.target.value }))} placeholder="Nome do Pai" className="h-7 text-[11px]" data-testid="hist-ext-filiacao_pai" />
                <Input value={externoForm?.filiacao_mae || ''} onChange={(e) => setExternoForm(p => ({ ...p, filiacao_mae: e.target.value }))} placeholder="Nome da Mãe" className="h-7 text-[11px]" data-testid="hist-ext-filiacao_mae" />
              </div>
            )} />
            <DataLine label="CPF" value={isExterno ? null : student.cpf} name="cpf" isExterno={isExterno} form={externoForm} setForm={setExternoForm} />
            <DataLine label="RG" value={isExterno ? null : student.rg} name="rg" isExterno={isExterno} form={externoForm} setForm={setExternoForm} />
            <DataLine label="Data de nascimento" value={isExterno ? null : formatDate(student.nascimento)} name="birth_date" isExterno={isExterno} form={externoForm} setForm={setExternoForm} placeholder="AAAA-MM-DD" />
            <DataLine label="Nacionalidade" value={isExterno ? null : student.nacionalidade} name="nacionalidade" isExterno={isExterno} form={externoForm} setForm={setExternoForm} />
            <DataLine label="Naturalidade" value={isExterno ? null : [student.naturalidade_municipio, student.naturalidade_uf].filter(Boolean).join(' / ')} name="municipio" isExterno={isExterno} form={externoForm} setForm={setExternoForm} />
            <DataLine label="Turma referência" value={isExterno ? null : student.turma_atual} name="turma_referencia" isExterno={isExterno} form={externoForm} setForm={setExternoForm} />
            <DataLine label="RA / RM" value={isExterno ? null : (student.ra || student.rm)} name="ra" isExterno={isExterno} form={externoForm} setForm={setExternoForm} />
            {!isExterno && <DataLine label="Data de matrícula" value={formatDate(student.matricula_em)} />}
          </div>
        </div>
      </section>

      {/* ═════════════ ÁREAS / COMPONENTES ═════════════ */}
      <section className="mt-5">
        <div className="flex items-baseline justify-between mb-2">
          <h2 className="doc-serif text-[13px] font-bold tracking-wide" style={{ color: 'var(--hist-purple)' }}>II — Áreas de Conhecimento e Componentes Curriculares</h2>
          <div className="text-[9px] uppercase tracking-[0.2em] text-slate-500 font-semibold">BNCC · Ensino Médio</div>
        </div>
        {editing && (
          <div className="flex justify-end mb-1 no-print">
            <Button size="sm" variant="outline" onClick={addManualRow} className="h-7 text-[11px]">+ Adicionar componente</Button>
          </div>
        )}
        <table className="doc-table" data-testid="hist-tabela-notas">
          <colgroup>
            <col style={{ width: '20%' }} />
            <col style={{ width: '28%' }} />
            <col style={{ width: '7%' }} />
            <col style={{ width: '7%' }} />
            <col style={{ width: '7%' }} />
            <col style={{ width: '8%' }} />
            <col style={{ width: '12%' }} />
            <col style={{ width: '11%' }} />
            {editing && <col style={{ width: '3%' }} />}
          </colgroup>
          <thead>
            <tr>
              <th rowSpan={2} className="text-left">Área de Conhecimento</th>
              <th rowSpan={2} className="text-left">Componente Curricular</th>
              <th colSpan={3}>Notas por Série</th>
              <th rowSpan={2}>Média</th>
              <th rowSpan={2}>Situação</th>
              <th rowSpan={2}>Carga Horária</th>
              {editing && <th rowSpan={2} className="no-print" />}
            </tr>
            <tr>
              <th>1ª</th>
              <th>2ª</th>
              <th>3ª</th>
            </tr>
          </thead>
          <tbody>
            {rowsByArea.length === 0 && !editing && (
              <tr><td colSpan={editing ? 9 : 8} className="text-center py-6 text-slate-400">Sem lançamentos disponíveis nesta fonte.</td></tr>
            )}
            {rowsByArea.map((group) => (
              <React.Fragment key={group.area}>
                {group.items.map((r, idx) => {
                  const realIdx = editing ? manualRows.findIndex(m => m.subject_name === r.subject_name && m.area === r.area) : -1;
                  const media = avg(r.nota_1, r.nota_2, r.nota_3);
                  const aprov = media != null && media >= 6;
                  return (
                    <tr key={`${group.area}-${idx}`}>
                      {idx === 0 && (
                        <td rowSpan={group.items.length} className="doc-area-cell align-middle text-left">
                          <div className="leading-tight">
                            <div className="doc-serif font-bold text-[10.5px]" style={{ color: 'var(--hist-purple)' }}>{(AREA_STYLES[group.area]?.label || group.area)}</div>
                            <div className="text-[8.5px] text-slate-500 uppercase tracking-wider mt-0.5">{AREA_STYLES[group.area]?.sub || ''}</div>
                          </div>
                        </td>
                      )}
                      <td className="text-left">
                        {editing ? (
                          <Input value={r.subject_name || ''} onChange={(e) => updateManualRow(realIdx, 'subject_name', e.target.value)} className="h-6 text-[10.5px]" />
                        ) : (
                          <span className="leading-snug">{r.subject_name}</span>
                        )}
                      </td>
                      {['nota_1', 'nota_2', 'nota_3'].map(k => (
                        <td key={k} className="text-center">
                          {editing ? (
                            <Input type="number" step="0.1" min="0" max="10" value={r[k] ?? ''}
                              onChange={(e) => updateManualRow(realIdx, k, e.target.value)}
                              className="h-6 text-[10.5px] text-center px-1" />
                          ) : (
                            <span className={`font-mono ${r[k] != null && Number(r[k]) < 6 ? 'text-rose-700 font-semibold' : 'text-slate-800'}`}>{fmt(r[k])}</span>
                          )}
                        </td>
                      ))}
                      <td className="text-center font-bold doc-serif" style={{ color: media == null ? '#94a3b8' : (aprov ? '#065f46' : '#9f1239') }}>{fmt(media)}</td>
                      <td className="text-center">
                        {media == null ? (
                          <span className="text-slate-400">—</span>
                        ) : (
                          <span className={`inline-block px-2 py-[1px] rounded-full text-[9px] font-semibold tracking-wide ${aprov ? 'bg-emerald-50 text-emerald-800 border border-emerald-200' : 'bg-rose-50 text-rose-800 border border-rose-200'}`}>{aprov ? 'Aprovado' : 'Recuperar'}</span>
                        )}
                      </td>
                      <td className="text-center text-slate-700">
                        {editing ? (
                          <Input type="number" value={r.carga_horaria ?? ''} onChange={(e) => updateManualRow(realIdx, 'carga_horaria', e.target.value)} className="h-6 text-[10.5px] text-center px-1" />
                        ) : (
                          r.carga_horaria != null ? `${r.carga_horaria} h` : '—'
                        )}
                      </td>
                      {editing && (
                        <td className="no-print text-center"><button onClick={() => removeManualRow(realIdx)} className="text-rose-600 text-[12px]">×</button></td>
                      )}
                    </tr>
                  );
                })}
              </React.Fragment>
            ))}
            {allRows.length > 0 && (
              <tr style={{ background: '#f8fafc' }}>
                <td colSpan={6} className="text-right font-semibold uppercase tracking-wider text-[9px]" style={{ color: 'var(--hist-purple)' }}>Carga horária total cumprida</td>
                <td />
                <td className="text-center font-bold doc-serif" style={{ color: 'var(--hist-purple)' }}>{chTotal.toLocaleString('pt-BR')} h</td>
                {editing && <td className="no-print" />}
              </tr>
            )}
          </tbody>
        </table>
      </section>

      {/* ═════════════ SÍNTESE FINAL ═════════════ */}
      <section className="mt-5 doc-no-break">
        <h2 className="doc-serif text-[13px] font-bold tracking-wide mb-2" style={{ color: 'var(--hist-purple)' }}>III — Síntese do Desempenho</h2>
        <div className="grid grid-cols-4 gap-3">
          <SummaryCell label="Situação Final" value={(
            <span className="doc-serif text-[16px] font-bold" style={{ color: sitAprovado ? '#065f46' : 'var(--hist-purple)' }}>{sitFinalText}</span>
          )} editing={editing} editor={editing && (
            <select value={manualMeta.situacao_final} onChange={(e) => setManualMeta(m => ({ ...m, situacao_final: e.target.value }))} className="mt-1 border rounded px-2 py-1 text-[11px] w-full">
              {['Cursando','Aprovado','Transferido','Reprovado','Concluído'].map(o => <option key={o} value={o}>{o}</option>)}
            </select>
          )} />
          <SummaryCell label="Média Geral" value={<span className="doc-serif text-[20px] font-bold text-slate-900">{fmt(mediaGeral)}</span>} />
          <SummaryCell label="Frequência" value={editing ? (
            <Input type="number" step="0.1" value={manualMeta.frequencia_pct ?? ''} onChange={(e) => setManualMeta(m => ({ ...m, frequencia_pct: e.target.value }))} className="h-7 text-[14px] font-bold text-center" />
          ) : <span className="doc-serif text-[20px] font-bold text-slate-900">{manualMeta.frequencia_pct != null ? `${Number(manualMeta.frequencia_pct).toFixed(1).replace('.', ',')}%` : '—'}</span>} />
          <SummaryCell label="Carga Horária Total" value={<span className="doc-serif text-[20px] font-bold text-slate-900">{chTotal.toLocaleString('pt-BR')} h</span>} />
        </div>
      </section>

      {/* ═════════════ OBSERVAÇÕES + VALIDAÇÃO ═════════════ */}
      <section className="mt-5 grid grid-cols-[2fr_1fr] gap-4 doc-no-break">
        <div className="doc-card px-4 py-3">
          <div className="text-[9px] uppercase tracking-[0.22em] font-semibold mb-2" style={{ color: 'var(--hist-purple)' }}>Observações</div>
          {editing ? (
            <textarea value={manualMeta.observacoes || ''} onChange={(e) => setManualMeta(m => ({ ...m, observacoes: e.target.value }))} className="w-full border rounded-md p-2 text-[11px] min-h-[60px]" />
          ) : (
            <div className="text-[10.5px] text-slate-700 leading-relaxed whitespace-pre-line">
              {manualMeta.observacoes || (sitAprovado ?
                "O(a) estudante concluiu todas as séries do Ensino Médio em conformidade com a legislação vigente, comprovando-se a integralização do currículo. Documento emitido conforme a Lei Federal nº 9.394/1996 (LDB) e normas do CEE/SP."
                : '—')}
            </div>
          )}
        </div>
        <div className="doc-card px-4 py-3">
          <div className="text-[9px] uppercase tracking-[0.22em] font-semibold mb-2" style={{ color: 'var(--hist-purple)' }}>Validação</div>
          <div className="flex items-center gap-3">
            <div className="text-[9.5px] text-slate-600 leading-snug flex-1">
              Autenticidade verificável via QR ou em <b style={{ color: 'var(--hist-purple)' }}>novodna.com.br/validar</b>
            </div>
            {emissao && validarUrl ? (
              <div className="p-1 bg-white border border-slate-300 rounded">
                <QRCodeCanvas value={validarUrl} size={70} level="H" />
              </div>
            ) : (
              <div className="w-[78px] h-[78px] border border-dashed border-slate-300 grid place-items-center text-[8.5px] text-slate-400 rounded">QR pós-emissão</div>
            )}
          </div>
        </div>
      </section>

      {/* ═════════════ ASSINATURAS ═════════════ */}
      <section className="mt-6 doc-no-break">
        <div className="grid grid-cols-2 gap-10 px-4">
          <SignatureBlock title="Secretaria Acadêmica" subtitle="Gerente de Organização Escolar" />
          <SignatureBlock title="Direção" subtitle="Diretor(a) de Escola" />
        </div>
        <div className="mt-4 text-center text-[9px] text-slate-600">
          {school.cidade_uf || 'Indaiatuba / SP'} · {emissao ? formatDateTime(emissao.emitido_em) : new Date().toLocaleDateString('pt-BR')}
        </div>
      </section>

      {/* ═════════════ FAIXA INSTITUCIONAL PREMIUM ═════════════ */}
      <section className="doc-band doc-no-break mt-5">
        {/* Hélice DNA refinada (esquerda) */}
        <div className="absolute left-4 top-1/2 -translate-y-1/2 hidden md:block" aria-hidden="true">
          <svg width="22" height="34" viewBox="0 0 22 34" fill="none" stroke="currentColor" strokeWidth="1.3" style={{ color: 'var(--hist-purple)' }}>
            <path d="M3 2 C 18 9, 18 17, 3 24" />
            <path d="M19 2 C 4 9, 4 17, 19 24" />
            <line x1="5" y1="6" x2="17" y2="6" />
            <line x1="6" y1="12" x2="16" y2="12" />
            <line x1="6" y1="18" x2="16" y2="18" />
            <line x1="5" y1="24" x2="17" y2="24" />
            <circle cx="11" cy="30" r="2" fill="#047857" stroke="none" />
          </svg>
        </div>
        {/* Hélice DNA refinada (direita) */}
        <div className="absolute right-4 top-1/2 -translate-y-1/2 hidden md:block" aria-hidden="true">
          <svg width="22" height="34" viewBox="0 0 22 34" fill="none" stroke="currentColor" strokeWidth="1.3" style={{ color: 'var(--hist-purple)' }}>
            <path d="M19 2 C 4 9, 4 17, 19 24" />
            <path d="M3 2 C 18 9, 18 17, 3 24" />
            <line x1="5" y1="6" x2="17" y2="6" />
            <line x1="6" y1="12" x2="16" y2="12" />
            <line x1="6" y1="18" x2="16" y2="18" />
            <line x1="5" y1="24" x2="17" y2="24" />
            <circle cx="11" cy="30" r="2" fill="#047857" stroke="none" />
          </svg>
        </div>
        <div className="doc-serif text-[12px] font-bold tracking-[0.22em] uppercase" style={{ color: 'var(--hist-purple)' }}>
          Documento Oficial
          <span className="doc-band-dot" />
          {(school.nome || 'Novo DNA Instituto de Aprendizagem').toUpperCase()}
        </div>
        <div className="doc-serif italic text-[11px] mt-1.5 text-slate-700">
          “Educação que transforma vidas através do conhecimento.”
        </div>
        <div className="text-[8.5px] uppercase tracking-[0.28em] text-slate-500 font-semibold mt-2">
          Documento validável digitalmente em <span style={{ color: 'var(--hist-purple)' }}>novodna.com.br/validar</span>
        </div>
      </section>

      {/* ═════════════ RODAPÉ INSTITUCIONAL ═════════════ */}
      <footer className="mt-3 pt-2 border-t border-slate-200 text-[8.5px] text-slate-500 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <span>www.novodna.com.br</span>
          <span className="text-slate-300">·</span>
          <span>{school.cidade_uf || 'Indaiatuba / SP'}</span>
          <span className="text-slate-300">·</span>
          <span>Ano letivo {new Date().getFullYear()}</span>
        </div>
        <div className="flex items-center gap-3">
          <span>Código <span className="font-mono text-slate-700">{(emissao?.token || '—').toUpperCase()}</span></span>
          <span className="text-slate-300">·</span>
          <span>Página 1 de 1</span>
        </div>
      </footer>
    </div>
  );
}

function DataLine({ label, value, strong = false, upper = false, name, isExterno = false, form, setForm, placeholder, colSpan = 1, renderExterno }) {
  const widthCls = colSpan === 2 ? 'col-span-2' : '';
  if (isExterno) {
    return (
      <div className={widthCls}>
        <div className="text-[8.5px] uppercase tracking-[0.18em] text-slate-500 font-semibold">{label}</div>
        {renderExterno ? renderExterno() : (
          <Input
            value={form?.[name] || ''}
            onChange={(e) => setForm(prev => ({ ...prev, [name]: e.target.value }))}
            placeholder={placeholder}
            className="h-7 text-[11.5px] mt-0.5"
            data-testid={`hist-ext-${name}`}
          />
        )}
      </div>
    );
  }
  return (
    <div className={widthCls}>
      <div className="text-[8.5px] uppercase tracking-[0.18em] text-slate-500 font-semibold">{label}</div>
      <div className={`text-[11.5px] mt-[2px] leading-tight ${strong ? 'font-bold' : 'font-medium'} ${upper ? 'uppercase tracking-wide' : ''} text-slate-900 border-b border-slate-200 pb-[1px]`}>
        {value || '—'}
      </div>
    </div>
  );
}

function SummaryCell({ label, value, editing, editor }) {
  return (
    <div className="doc-card px-3 py-2 text-center">
      <div className="text-[8.5px] uppercase tracking-[0.18em] text-slate-500 font-semibold">{label}</div>
      <div className="mt-1">{value}</div>
      {editing && editor}
    </div>
  );
}

function SignatureBlock({ title, subtitle }) {
  return (
    <div className="text-center">
      <div className="h-[34px] border-b border-slate-500" />
      <div className="mt-1.5 doc-serif text-[11px] font-bold uppercase tracking-[0.16em]" style={{ color: 'var(--hist-purple)' }}>{title}</div>
      <div className="text-[9px] text-slate-500 italic mt-0.5">{subtitle}</div>
    </div>
  );
}

function PField({ bullet = false, label, value, strongUpper = false, name, isExterno = false, form, setForm, placeholder }) {
  if (isExterno) {
    return (
      <li className="text-[12px]">
        <span className="text-purple-700">•</span>{' '}
        <span className="text-slate-600">{label}:</span>
        <Input
          value={form?.[name] || ''}
          onChange={(e) => setForm(prev => ({ ...prev, [name]: e.target.value }))}
          placeholder={placeholder}
          className="h-7 text-[12px] mt-0.5"
          data-testid={`hist-ext-${name}`}
        />
      </li>
    );
  }
  return (
    <li className="text-[12px]">
      {bullet && <span className="text-purple-700">•</span>}{' '}
      <span className="text-slate-600">{label}:</span>{' '}
      <b className={`text-slate-800 ${strongUpper ? 'uppercase' : ''}`}>{value || '—'}</b>
    </li>
  );
}

function KpiCard({ color, title, icon, value, sub, editor, editing }) {
  const colorMap = {
    purple: { ring: 'border-purple-100', text: 'text-purple-700' },
    emerald: { ring: 'border-emerald-100', text: 'text-emerald-600' },
    sky: { ring: 'border-sky-100', text: 'text-sky-600' },
  };
  const c = colorMap[color] || colorMap.purple;
  return (
    <div className={`rounded-xl border ${c.ring} bg-white px-4 py-3`}>
      <div className={`flex items-center gap-1.5 text-[10px] uppercase tracking-[0.14em] font-bold ${c.text}`}>
        <span className="inline-block w-4 h-4 grid place-items-center">{icon}</span>{title}
      </div>
      <div className="mt-1">{value}</div>
      {editing && editor}
      {!editing && <div className="text-[10px] text-slate-500 mt-0.5">{sub}</div>}
    </div>
  );
}

function Field({ label, value, strong = false }) {
  return (
    <div>
      <span className="text-[9px] uppercase tracking-widest text-slate-500">{label}</span>
      <div className={`${strong ? 'font-bold text-slate-900' : 'text-slate-700'} text-sm leading-tight`}>{value || '—'}</div>
    </div>
  );
}

function ExtField({ label, name, form, setForm, strong = false, placeholder }) {  // eslint-disable-line no-unused-vars
  const value = form?.[name] || '';
  return (
    <div>
      <span className="text-[9px] uppercase tracking-widest text-slate-500">{label}</span>
      <Input
        value={value}
        onChange={(e) => setForm(prev => ({ ...prev, [name]: e.target.value }))}
        placeholder={placeholder}
        className={`h-7 text-[12px] ${strong ? 'font-bold' : ''}`}
        data-testid={`hist-ext-${name}`}
      />
    </div>
  );
}

function Box({ label, children }) {
  return (
    <div className="rounded-lg border border-slate-200 bg-slate-50/50 p-2">
      <div className="text-[9px] uppercase tracking-widest text-slate-500 mb-0.5">{label}</div>
      <div>{children}</div>
    </div>
  );
}

function formatDate(s) {
  if (!s) return null;
  try {
    const d = new Date(s);
    if (isNaN(d.getTime())) return s;
    return d.toLocaleDateString('pt-BR');
  } catch { return s; }
}
function formatDateTime(s) {
  if (!s) return '';
  try {
    const d = new Date(s);
    return d.toLocaleString('pt-BR');
  } catch { return s; }
}
